import { ItemsIcon } from "@xola/icons";
import { flash, SubmitButton } from "@xola/ui-kit";
import { useInterval } from "ahooks";
import { SecondaryButton } from "common";
import { FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { CatalogItemObject, Merchandise, MerchandiseCatalogItem } from "types";
import { number, object, string } from "yup";
import { EditVariantModal } from "./Inventory/EditVariantModal";
import { ManageInventoryModal } from "./Inventory/ManageInventoryModal";
import { SingleItemInventoryTable } from "./Inventory/SingleItemInventoryTable";
import {
    VariantGroupItemInventoryTable,
    VariantGroupItemInventoryTableHandle,
} from "./Inventory/VariantGroupItemInventoryTable";
import { ItemsSyncInProgressNotice } from "./ItemsSyncInProgressNotice";
import { getItemsInitialValues, MerchandiseItemsFormValues } from "./merchandiseHelper";
import { api } from "@/api/api";
import { Form } from "@/components/Common/Form";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { ScrollToFieldError } from "@/helpers/validationHelper";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResource } from "@/hooks/useResource";
import { useModal } from "@/hooks/useVisibility";
import {
    getPublicVariantCount,
    getSingleVariantCatalogItem,
    hasVariantsEnabled,
    isInventoryTracked,
    MERCHANDISE_CACHE_KEY,
} from "@/models/Merchandise";

const getValidationSchema = (hasVariants: boolean) => {
    return object().shape({
        sku: string(),
        barcode: string(),
        price: hasVariants
            ? number().nullable()
            : number().min(0, "Price must be 0 or greater").required("Price is required"),
        quantity: number(),
    });
};

const getIsMerchandiseUpdatePending = (merchandise?: Merchandise | null): boolean => {
    return merchandise?.updatesPending ?? false;
};

const POLLING_INTERVAL_MS = 1000;
const MAX_PENDING_POLL_ATTEMPTS = 120;

interface MerchandiseItemsProps {
    merchandiseId: string;
}

export const MerchandiseItems = ({ merchandiseId }: MerchandiseItemsProps) => {
    const invalidate = useInvalidate();
    const formikRef = useRef<FormikProps<MerchandiseItemsFormValues>>(null);
    const variantGroupTableRef = useRef<VariantGroupItemInventoryTableHandle>(null);
    const [editingCatalogItemId, setEditingCatalogItemId] = useState<string | null>(null);
    const [refetchInterval, setRefetchInterval] = useState<number | false>(false);
    const manageInventoryModal = useModal(false);
    // Tracks whether polling has settled (updatesPending went from true→false).
    // Prevents stale cache writes from useProducts/populateResource from restarting the polling cycle.
    const pollingSettledRef = useRef(false);
    const pollingAttemptRef = useRef(0);

    const { data: merchandise } = useResource<Merchandise>([MERCHANDISE_CACHE_KEY, merchandiseId], null, {
        enabled: !!merchandiseId,
        staleTime: 0,
        refetchInterval,
    });

    const hasVariants = merchandise ? hasVariantsEnabled(merchandise) : false;
    const isMerchandiseUpdatePending = getIsMerchandiseUpdatePending(merchandise);
    const isTrackedInventory = merchandise ? isInventoryTracked(merchandise) : false;

    useInterval(
        () => {
            pollingAttemptRef.current += 1;

            if (pollingAttemptRef.current >= MAX_PENDING_POLL_ATTEMPTS) {
                setRefetchInterval(false);
                pollingSettledRef.current = true;
            }
        },
        isMerchandiseUpdatePending && !!refetchInterval ? POLLING_INTERVAL_MS : undefined,
    );

    useEffect(() => {
        const shouldPoll = isMerchandiseUpdatePending;

        if (shouldPoll && !refetchInterval && !pollingSettledRef.current) {
            pollingAttemptRef.current = 0;
            setRefetchInterval(POLLING_INTERVAL_MS);
        } else if (!shouldPoll && refetchInterval) {
            setRefetchInterval(false);
            pollingAttemptRef.current = 0;
            pollingSettledRef.current = true;
            invalidate(MERCHANDISE_CACHE_KEY, merchandiseId);
            invalidate("catalogItems", merchandiseId);
        }
    }, [isMerchandiseUpdatePending, refetchInterval, invalidate, merchandiseId]);

    const isWaiting = isMerchandiseUpdatePending && !pollingSettledRef.current;

    const { data: catalogItems } = useAutoFetchCollection<MerchandiseCatalogItem>(
        "catalogItems",
        {
            product: merchandiseId,
            object: CatalogItemObject.WARE_CATALOG_ITEM,
        },
        {
            enabled: !!merchandise && !isWaiting,
        },
    );

    const catalogItem = getSingleVariantCatalogItem(catalogItems);
    const publicVariantCount = getPublicVariantCount(catalogItems);

    const { mutateAsync: saveItems } = useMutation(async (values: MerchandiseItemsFormValues) => {
        if (!merchandise) {
            return [];
        }

        const promises = [];

        if (catalogItem) {
            const initialPrice = catalogItem.prices?.perUnit?.price ?? catalogItem.price ?? null;
            const initialSku = catalogItem.sku ?? "";
            const initialBarcode = catalogItem.barcode ?? "";

            const payload: Record<string, unknown> = {};

            if ((values.price ?? null) !== initialPrice) {
                payload.pricePerUnit = { price: values.price };
            }
            if (values.sku !== initialSku) {
                payload.sku = values.sku.trim();
            }
            if (values.barcode !== initialBarcode) {
                payload.barcode = values.barcode.trim();
            }

            if (Object.keys(payload).length > 0) {
                promises.push(api.put(`catalogItems/${catalogItem.id}`, payload));
            }
        }

        const response = await Promise.all(promises);
        flash.success("Items saved successfully");
        invalidate(MERCHANDISE_CACHE_KEY, merchandise.id);
        invalidate("catalogItems", catalogItem?.id);
        return response;
    });

    const handleSave = async (values: MerchandiseItemsFormValues) => {
        if (!merchandise) {
            return;
        }

        const hasEmptyVariantSkus =
            hasVariants &&
            (catalogItems ?? []).some((item) => {
                if (!item.id) {
                    return false;
                }

                const draft = values.variantItems[item.id];
                const sku = draft?.sku !== undefined ? draft.sku : item.sku ?? "";
                return sku.trim().length === 0;
            });

        if (hasEmptyVariantSkus) {
            return;
        }

        if (hasVariants) {
            pollingSettledRef.current = false;
            await variantGroupTableRef.current?.saveChangedCatalogItems();
            return;
        }

        await saveItems(values);
    };

    if (!merchandise || (!hasVariants && !catalogItem)) {
        return null;
    }

    return (
        <XolaFormik
            enableReinitialize
            initialValues={getItemsInitialValues(catalogItem)}
            validationSchema={getValidationSchema(hasVariants)}
            innerRef={formikRef}
            onSubmit={handleSave}
        >
            {({ isSubmitting, values, setFieldValue, handleReset, errors, dirty, isValid }) => {
                return (
                    <>
                        <Form>
                            <ScrollToFieldError />
                            <ContentLayout isWide>
                                <div className="space-y-4">
                                    <h3>Items</h3>

                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between space-x-2">
                                            <h6 className="font-bold">Pricing and Inventory</h6>
                                            {isTrackedInventory && (
                                                <SecondaryButton size="small" onClick={manageInventoryModal.show}>
                                                    <ItemsIcon size="small" className="mr-1" />
                                                    Manage Inventory
                                                </SecondaryButton>
                                            )}
                                        </div>
                                        {isWaiting && <ItemsSyncInProgressNotice />}
                                        {!isWaiting && !hasVariants && catalogItem && (
                                            <SingleItemInventoryTable
                                                values={values}
                                                catalogItem={catalogItem}
                                                merchandise={merchandise}
                                                setFieldValue={setFieldValue}
                                                errors={errors}
                                                onEdit={(catalogItemId) => {
                                                    setEditingCatalogItemId(catalogItemId);
                                                }}
                                                onSkuChange={(value) => setFieldValue("sku", value)}
                                                onBarcodeChange={(value) => setFieldValue("barcode", value)}
                                            />
                                        )}
                                    </div>

                                    {hasVariants && !isWaiting && (
                                        <VariantGroupItemInventoryTable
                                            ref={variantGroupTableRef}
                                            merchandise={merchandise}
                                            catalogItems={catalogItems}
                                        />
                                    )}
                                </div>
                                <ContentLayout.Footer preferenceSidebar>
                                    <SubmitButton isLoading={isSubmitting || isWaiting} disabled={!dirty || !isValid}>
                                        Save
                                    </SubmitButton>
                                    <SecondaryButton type="reset" onClick={handleReset}>
                                        Cancel
                                    </SecondaryButton>
                                </ContentLayout.Footer>
                            </ContentLayout>
                        </Form>
                        {!hasVariants && editingCatalogItemId && (
                            <EditVariantModal
                                isOpen={!!editingCatalogItemId}
                                catalogItemId={editingCatalogItemId}
                                merchandise={merchandise}
                                publicVariantCount={publicVariantCount}
                                onClose={() => setEditingCatalogItemId(null)}
                            />
                        )}
                        <ManageInventoryModal
                            isOpen={manageInventoryModal.isVisible}
                            merchandise={merchandise}
                            onClose={manageInventoryModal.hide}
                        />
                    </>
                );
            }}
        </XolaFormik>
    );
};
